// ─────────────────────────────────────────────────────────────────────────────
// deltaHandoff.ts — THE COMPANIES BUCK HANDED OVER, READ ONCE.
//
// `GET /api/delta/handoff`. The payload was composed and measured against Buck's
// live T2 before a line of this was written
// (`Rexy/scratchpad/delta_interface/selector_payload.py`), so the shape below is
// transcribed from records that exist rather than designed and then hoped for:
//
//   212 companies with a current Buck row · 140 qualified · 72 watchlist
//   140/140 qualified carry a sector AND region   → all researchable
//   103/140 carry a phone the owner can dial      → 37 CANNOT BE DIALLED
//   104/140 carry a business name in words        → 36 have none
//
// ── THE ROUTE DOES NOT EXIST YET AND THIS CLIENT SAYS SO BY NAME ───────────
// Forge owns the backend. Until it lands, an honest surface must not paint
// "Buck handed over nothing" — an EMPTY LIST and a MISSING ROUTE are different
// facts and the owner acts differently on each. So the three are kept apart, the
// way the transport chip kept 401 from 404:
//   'unread'    we could not read it (network, parse, 5xx)
//   'absent'    the route answered 404 — THIS BUILD DOES NOT PUBLISH THE HANDOFF
//   'read'      it answered, and `rows` is what Buck has (possibly zero)
//
// ── A SLUG IS AN ADDRESS, NOT A NAME ───────────────────────────────────────
// `business` is `UNKNOWN` on 36 of the qualified and the slug is NOT promoted
// into a name anywhere in this file. A row rendering "triple-s-motors" is a
// path leaking onto a screen.
// ─────────────────────────────────────────────────────────────────────────────

import { apiBase, authToken } from './apiBase';

export const DELTA_HANDOFF_PATH = '/delta/handoff';

/** The one value that means "a record exists and it does not say". Never `''`,
 *  never `null` rendered as a blank: missing is never neutral. */
export const UNKNOWN = 'UNKNOWN';

export interface HandoffCompany {
  company_id: string;
  folder: string;
  slug: string;
  business: string;
  sector: string;
  region: string;
  qualification_state: string;
  fit: number | null;
  /** Buck's verdict in the owner's words ("Fit: Unknown — no size evidence"). */
  fit_flag?: string;
  phone: string;
  contact_confidence: number | null;
  buyer_role_state: string;
  website_state: string;
  checked_at: string;
}

export type HandoffPhase = 'unread' | 'absent' | 'read';

export interface HandoffState {
  phase: HandoffPhase;
  rows: HandoffCompany[];
  /** A FEW WORDS. The reasoning lives in this comment block, not on a screen. */
  note: string;
}

export const HANDOFF_UNREAD: HandoffState = { phase: 'unread', rows: [], note: 'UNREAD' };

function coerce(raw: any): HandoffCompany | null {
  const id = String(raw?.company_id || '').trim();
  if (!id) return null;                     // a row with no identity is not a row
  const str = (v: any) => {
    const s = String(v == null ? '' : v).trim();
    return s || UNKNOWN;
  };
  const num = (v: any) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  return {
    company_id: id,
    folder: String(raw?.folder || '').trim(),
    slug: String(raw?.slug || '').trim(),
    business: str(raw?.business),
    sector: str(raw?.sector),
    region: str(raw?.region),
    qualification_state: str(raw?.qualification_state),
    fit: num(raw?.fit),
    fit_flag: str(raw?.fit_flag),
    phone: str(raw?.phone),
    contact_confidence: num(raw?.contact_confidence),
    buyer_role_state: str(raw?.buyer_role_state),
    website_state: str(raw?.website_state),
    checked_at: str(raw?.checked_at),
  };
}

export async function readHandoff(): Promise<HandoffState> {
  let res: Response;
  try {
    res = await fetch(apiBase() + DELTA_HANDOFF_PATH, {
      headers: { Authorization: 'Bearer ' + authToken() },
    });
  } catch {
    return HANDOFF_UNREAD;
  }
  if (res.status === 404) {
    return { phase: 'absent', rows: [], note: 'THIS BUILD DOES NOT PUBLISH THE HANDOFF' };
  }
  if (!res.ok) return HANDOFF_UNREAD;
  let body: any;
  try {
    body = await res.json();
  } catch {
    return HANDOFF_UNREAD;
  }
  const raw = Array.isArray(body) ? body : body?.rows;
  // A 200 WHOSE BODY HAS NO ROWS KEY IS UNREAD, NOT EMPTY. `liveKnown` on the
  // Buck strip is the same distinction: read the KEY, not the value.
  if (!Array.isArray(raw)) return HANDOFF_UNREAD;
  const rows = raw.map(coerce).filter((r): r is HandoffCompany => r !== null);
  return { phase: 'read', rows, note: '' };
}

/** EVERY ROW BUCK HANDS OVER. ⭐⭐⭐ FIT IS NOT A GATE (owner ruling 2026-10-06):
 *  every company Buck researches comes to sales, low and unknown Fit included, and
 *  the caller is SHOWN its Fit flag instead. This used to keep `qualified` only and
 *  grey the watchlist out — a Fit gate on the sales screen. Membership is Buck's. */
export function selectable(rows: HandoffCompany[]): HandoffCompany[] {
  return rows || [];
}

/** Can the research seam be called for this row AT ALL? Measured against the
 *  frozen seam's own rule, not a preference: `2 Sector Pain` refuses an empty
 *  `{sector}` or `{region}` with `unfilled_hole` BEFORE the fill loop and never
 *  substitutes `""`. */
export function researchable(row: HandoffCompany): boolean {
  return !!row && row.sector !== UNKNOWN && row.region !== UNKNOWN;
}

/** Can the owner dial from the Calls tab without going to look? */
export function dialable(row: HandoffCompany): boolean {
  return !!row && row.phone !== UNKNOWN;
}

/** What the Calls tab says instead of an empty slot. An empty field reads as
 *  "no number exists"; the true state is "Buck's contact route has none", which
 *  is a discovery question, not a conclusion. */
export const NO_PHONE_LINE = 'Buck recorded no public phone for this company';

/** The name to render — never the slug. */
export function displayName(row: HandoffCompany): string {
  return row && row.business !== UNKNOWN ? row.business : '[no business name recorded]';
}
